import { React, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import moment from 'moment';

import { useLanguage } from '../../../Context/LanguageContext';
import FetchModule from '../../Share/Network/FetchModule';
import { toBirthDay, toGender, digitOf } from '../../../Utils/socialSecurityUtils';

import RegistrationLoading from './RegistrationLoading.js';
import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';
import LinkoCalendarInput from '../../../MemberRegistration/Component/LinkoPayCalendarInput.js';
import { ReactComponent as PlusSvg } from '../../../image/Plus.svg';

const LinkoPayModal = (props) => {
  const {
    userData,
    merchantUid,
    cancel,
    closeFn
  } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [data, setData] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [prevSalesColor, setPrevSalesColor] = useState(theme.text);
  const [prevSalesDDayColor, setPrevSalesDDayColor] = useState(theme.white);

  const [memberships, setMemberships] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState({ id: 0, name: '선택' });

  const [startDate, setStartDate] = useState(moment().format('YYYY.MM.DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY.MM.DD'));
  const [approvalDate, setApprovalDate] = useState(moment().format('YYYY.MM.DD'));
  const [totalMax, setTotalMax] = useState(-1);

  // 링코페이 결제 정보 불러오기 //
  useEffect(() => {
    if (merchantUid === undefined) return;

    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postData('sales/merchant_data/' + merchantUid, 'POST');
      if (responseData.status === 200) setData(responseData.data[0]);
      else if (responseData.status === 401) {
        alert(t('waiting.onSitePaymentModal.alertLoginRequired'));
        history.push('/login');
      }
    })();
  }, [merchantUid])

  // 카테고리 및 회원권 데이터 불러오기 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.getDataVersion2('membership/', 'GET');
      if (responseData.status === 200) {
        setMemberships(responseData.data.memberShips);
        setCategories(responseData.data.categories);
      }
      else if (responseData.status === 401) history.push('/login');
    })();
  }, [history])

  // 회원권 자동 세팅 //
  useEffect(() => {
    if (data === undefined) return;

    const willSelectMembership = memberships.find(m => m.id === data.membershipId) || { id: 0, name: '선택' };

    setTimeout(() => {
      setSelectedMembership(willSelectMembership);
      setStartDate(moment().format('YYYY.MM.DD'));

      let division = 'days';
      if (data.validityUnit === 'year') division = 'year';
      else if (data.validityUnit === 'month') division = 'month';

      setEndDate(moment().add(data.validity, division).subtract(1, 'days').format('YYYY.MM.DD'));
      setApprovalDate(moment().format('YYYY.MM.DD'));
    }, 150);

    setTotalMax(data.totalMax);
  }, [categories, memberships, data])

  // 회원권 종료일 자동 세팅 //
  useEffect(() => {
    if (startDate === '' || selectedMembership.id === 0) return;
    setEndDate(moment(startDate, 'YYYY.MM.DD').add(selectedMembership.validity, selectedMembership.validityUnit).subtract(1, 'days').format('YYYY.MM.DD'));
  }, [startDate, selectedMembership]);

  // 회원권 총 횟수 세팅 //
  useEffect(() => {
    if (selectedMembership.id === 0) setTotalMax(-1);
    else setTotalMax(selectedMembership.totalMax);
  }, [selectedMembership])

  // 이전 등록 회원권 정보 뷰 정리 //
  function onCalcPrevSalesInfo(className) {
    if (userData === undefined || userData.prevSales === undefined || userData.prevMemberShip === undefined) return '';

    if (className === 'MemberShipName') return userData.prevSales.itemName;
    else if (className === 'Date') {
      const fmt = t('calendarInput.rangeDateFormat');
      return moment(userData.prevUsage.startDate).format(fmt) + ' - ' + moment(userData.prevUsage.endDate).format(fmt);
    }
    else if (className === 'TotalPrice') {
      if (userData.prevUsage.selectedPaymentOptions === undefined || userData.prevUsage.selectedPaymentOptions.length === 0) {
        if (userData.prevUsage.cardPrice === 0 && userData.prevUsage.cashPrice === 0) return t('waiting.onSitePaymentModal.free');
        else if (userData.prevUsage.cardPrice === 0) return userData.prevUsage.cashPrice.toLocaleString('ko-KR') + ' - ' + t('waiting.onSitePaymentModal.cashDisplay');
        else if (userData.prevUsage.cashPrice === 0) return userData.prevUsage.cardPrice.toLocaleString('ko-KR') + ' - ' + t('waiting.onSitePaymentModal.cardDisplay');
        else return (parseInt(userData.prevUsage.cashPrice) + parseInt(userData.prevUsage.cardPrice)).toLocaleString('ko-KR') + ' - ' + t('waiting.onSitePaymentModal.mixedPayment');
      }
      else {
        const allSame = userData.prevUsage.selectedPaymentOptions.every(item => item.paymentOptionName === userData.prevUsage.selectedPaymentOptions[0].name);
        const totalPrice = userData.prevUsage.selectedPaymentOptions.reduce((sum, item) => sum + item.price, 0);
        if (allSame) return totalPrice.toLocaleString('ko-KR') + ' - ' + userData.prevUsage.selectedPaymentOptions[0].paymentOptionName;
        else return totalPrice.toLocaleString('ko-KR') + ' - ' + t('waiting.onSitePaymentModal.mixedPayment');
      }
    }
    else if (className === 'NotReceivedPrice') return userData.prevUsage.notReceivedPrice.toLocaleString('ko-KR');
    else if (className === 'Validity') {
      const diffDay = moment(userData.prevUsage.endDate).diff(moment(), 'day');
      if (diffDay > 0) return 'D - ' + diffDay;
      else if (diffDay === 0) return 'D - day';
      else return 'D + ' + Math.abs(diffDay);
    }
    else if (className === 'TotalMax') {
      if (userData.prevUsage.totalMax === -1) return userData.prevUsage.courseTakeCount + '/∞';
      else return userData.prevUsage.courseTakeCount + '/' + userData.prevUsage.totalMax;
    }
  }

  // 이전 등록 회원권 정보 호버링 //
  function onMouseOverPrevSales() { setPrevSalesColor(theme.primary); setPrevSalesDDayColor(theme.primary); }

  // 이전 등록 회원권 정보 호버링 해제 //
  function onMouseLeavePrevSales() { setPrevSalesColor(theme.text); setPrevSalesDDayColor(theme.white); }

  // 이전 등록 회원권 정보 클릭시 //
  function onClickPrevSales() {
    if (userData === undefined) return;
    const willSelectMembership = memberships.find(m => m.id === userData.prevMemberShip.id) || { id: 0, name: '선택' };
    setSelectedMembership(willSelectMembership);
    setTotalMax(userData.prevUsage.totalMax);
  }

  // 포인트 차감 후 링코페이 최종 결제 금액 계산 //
  function calcLinkPayFinalPrice() {
    const membershipPrice = selectedMembership.id !== 0 ? selectedMembership.price : 0;
    const pointAmount = userData.point || 0;
    return Math.max(membershipPrice - pointAmount, 0);
  }

  // 등록하기 버튼 클릭시 //
  function onClickRegistrationButton() {
    if (selectedMembership.id === 0) {
      alert(t('waiting.onSitePaymentModal.alertSelectMembership'));
      return;
    }

    const checkStartDate = moment(startDate.replaceAll('.', '-') + 'T00:00:00');
    const checkEndDate = moment(endDate.replaceAll('.', '-') + 'T23:59:59');

    if (checkEndDate.isBefore(checkStartDate)) {
      alert(t('waiting.onSitePaymentModal.alertEndDateInvalidShort'));
      return;
    }

    const pointAmount = userData.point || 0;
    const linkPayAmount = calcLinkPayFinalPrice();
    const digit = digitOf(userData.birthDay);
    const birthDay = toBirthDay(digit, userData.birthDay.slice(0, 6));
    const gender = toGender(digit);

    // 결제수단 내역 구성 ( 링코페이 → 포인트 순, 0원 항목 제외 ) //
    const membershipPaymentOptions = [];

    if (linkPayAmount > 0) membershipPaymentOptions.push({ id: 0, name: '링코페이', price: linkPayAmount });
    if (pointAmount > 0) membershipPaymentOptions.push({ id: 0, name: '포인트', price: pointAmount });

    const registData = {
      name: userData.userName,
      birthDay,
      gender,
      phone: userData.phone,
      linkCoachId: userData.userId,
      storeId: parseInt(window.sessionStorage.getItem('StoreId')),
      memberShipName: selectedMembership.name,
      profile: userData.profile,
      memberDetail: '',
      approval: moment().format('YYYY-MM-DDTHH:mm:ss'),
      totalMax,
      startDate: moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
      endDate: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
      memberShipId: selectedMembership.id,
      cardPrice: linkPayAmount,
      cashPrice: 0,
      discount: 0,
      notReceived: 0,
      point: pointAmount,
      membershipPaymentOptions
    };

    onClickPaymentOnPayple(registData);
  }

  // 링코페이 결제자의 신청을 승인시 //
  function onClickPaymentOnPayple(registData) {
    if (isLoading) {
      alert(t('waiting.onSitePaymentModal.alertPaymentInProgress'));
      return;
    }

    setIsLoading(true);
    registData.merchantUid = merchantUid;

    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('member/registration/waiting/linkpay', 'POST', registData);

      if (responseData.status === 200) {
        cancel(responseData.data);
        setIsLoading(false);
      }
      else if (responseData.status === 201) { alert(t('waiting.onSitePaymentModal.alertNotLinkCoachMember')); setIsLoading(false); }
      else if (responseData.status === 202) { alert(t('waiting.onSitePaymentModal.alertDataNotFound')); setIsLoading(false); }
      else if (responseData.status === 203) { alert(t('waiting.onSitePaymentModal.alertDataNotFound')); setIsLoading(false); }
      else if (responseData.status === 204) { alert(t('waiting.onSitePaymentModal.alertCardNotRegistered')); setIsLoading(false); }
      else if (responseData.status === 205) { alert(t('waiting.onSitePaymentModal.alertPaymentError')); setIsLoading(false); }
      else if (responseData.status === 206) { alert(t('waiting.onSitePaymentModal.alertSubscriptionNoChange')); setIsLoading(false); }
      else { alert(responseData.error); setIsLoading(false); }
    })();
  }

  return (
    <TotalModalWrapper>
      {isLoading && <RegistrationLoading />}
      <ModalView>
        <MembershipBoxInnerWrapper onClick={onClickPrevSales} onMouseOver={onMouseOverPrevSales} onMouseLeave={onMouseLeavePrevSales}>
          <TextWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} cursor='default'>{userData?.userName} - {t('waiting.onSitePaymentModal.membershipPaymentApproval')}</Text>
            <CloseButton onClick={closeFn}>
              <StyledPlusIcon />
            </CloseButton>
          </TextWrapper>
          {userData?.prevSales !== undefined && userData?.prevMemberShip !== undefined && <InfoTopLevelWrapper>
            <TitleWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.prevMembershipInfo')}</Text>
            </TitleWrapper>
            <DivisionLine margin={0} />
            <InfoBox>
              <RowWrapper>
                <PrevSalesInfoBoxWrapper marginRight={0}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} cursor='default'>{t('waiting.onSitePaymentModal.membership')}</Text>
                  <PrevSalesTextWrapper>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={prevSalesColor} cursor='pointer'>{onCalcPrevSalesInfo('MemberShipName')}</Text>
                  </PrevSalesTextWrapper>
                </PrevSalesInfoBoxWrapper>
                <PrevSalesInfoBoxWrapper marginRight={0}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} cursor='default'>{t('waiting.onSitePaymentModal.period')}</Text>
                  <PrevSalesTextWrapper>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={prevSalesColor} cursor='pointer'>{onCalcPrevSalesInfo('Date')}</Text>
                  </PrevSalesTextWrapper>
                </PrevSalesInfoBoxWrapper>
              </RowWrapper>
              <RowWrapper onClick={onClickPrevSales} onMouseOver={onMouseOverPrevSales} onMouseLeave={onMouseLeavePrevSales}>
                <RowInnerWrapper>
                  <PrevSalesInfoBoxWrapper width={150} marginRight={10}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} cursor='default'>{t('waiting.onSitePaymentModal.paymentAmount')}</Text>
                    <PrevSalesTextWrapper>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={prevSalesColor} cursor='pointer'>{onCalcPrevSalesInfo('TotalPrice')}</Text>
                    </PrevSalesTextWrapper>
                  </PrevSalesInfoBoxWrapper>
                  <PrevSalesInfoBoxWrapper width={80} marginRight={13}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} cursor='default'>{t('waiting.onSitePaymentModal.notReceived')}</Text>
                    <PrevSalesTextWrapper>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={prevSalesColor} cursor='pointer'>{onCalcPrevSalesInfo('NotReceivedPrice')}</Text>
                    </PrevSalesTextWrapper>
                  </PrevSalesInfoBoxWrapper>
                </RowInnerWrapper>
                <RowInnerWrapper>
                  <PrevSalesInfoBoxWrapper width={105} marginRight={30}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} cursor='default'>{t('waiting.onSitePaymentModal.remainingDays')}</Text>
                    <PrevSalesDDayWrapper>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={prevSalesDDayColor} cursor='pointer'>{onCalcPrevSalesInfo('Validity')}</Text>
                    </PrevSalesDDayWrapper>
                  </PrevSalesInfoBoxWrapper>
                  <PrevSalesInfoBoxWrapper width={55}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText} cursor='default'>{t('waiting.onSitePaymentModal.courseCount')}</Text>
                    <PrevSalesTextWrapper>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={prevSalesColor} cursor='pointer'>{onCalcPrevSalesInfo('TotalMax')}</Text>
                    </PrevSalesTextWrapper>
                  </PrevSalesInfoBoxWrapper>
                </RowInnerWrapper>
              </RowWrapper>
            </InfoBox>
          </InfoTopLevelWrapper>}
        </MembershipBoxInnerWrapper>
        <LayerTopLevelWrapper>
          <TextWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.paymentInfo')}</Text>
          </TextWrapper>
          <DivisionLine margin={10} />
          <LinkPayWrapper>
            <LayerWrapper padding={10} isSpaceBetween={true}>
              <LickoPayUnitInputWrapper gap={10} style={{ flex: 1, minWidth: 0, overflow: 'hidden', flexShrink: 1 }}>
                <MembershipNameWrapper>
                  <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text} cursor='default'>{selectedMembership.name}</Text>
                </MembershipNameWrapper>
                <SubscriptionWrapper>
                  {userData.isSubscription && <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.white} cursor='default'>{t('waiting.onSitePaymentModal.subscription')}</Text>}
                </SubscriptionWrapper>
              </LickoPayUnitInputWrapper>
              <LickoPayUnitInputWrapper gap={5}>
                <TextWrapper width={75}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{selectedMembership.id !== 0 ? selectedMembership.price.toLocaleString('ko-KR') : '0'}</Text>
                </TextWrapper>
                <TextWrapper width={35}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.wonUnit')}</Text>
                </TextWrapper>
              </LickoPayUnitInputWrapper>
            </LayerWrapper>
            {!userData.isSubscription && <LayerWrapper padding={10} isSpaceBetween={true}>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.pointUsageAmount')}</Text>
              <LickoPayUnitInputWrapper gap={5}>
                <TextWrapper width={75}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subKey} cursor='default'>- {userData.point ? userData.point.toLocaleString('ko-KR') : '0'}</Text>
                </TextWrapper>
                <TextWrapper width={35}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subKey} cursor='default'>point</Text>
                </TextWrapper>
              </LickoPayUnitInputWrapper>
            </LayerWrapper>}
            <LayerWrapper padding={10} isSpaceBetween={true}>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.finalPaymentAmount')}</Text>
              <LickoPayUnitInputWrapper gap={5}>
                <TextWrapper width={75}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>{calcLinkPayFinalPrice().toLocaleString('ko-KR')}</Text>
                </TextWrapper>
                <TextWrapper width={35}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.wonUnit')}</Text>
                </TextWrapper>
              </LickoPayUnitInputWrapper>
            </LayerWrapper>
            <LayerWrapper padding={10} isSpaceBetween={true}>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.paymentRequestDate')}</Text>
              <LickoPayUnitInputWrapper gap={5}>
                <TextWrapper width={75}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{moment(approvalDate).format(t('calendarInput.rangeDateFormat'))}</Text>
                </TextWrapper>
                <TextWrapper width={35} />
              </LickoPayUnitInputWrapper>
            </LayerWrapper>
            <LayerWrapper padding={10} gap={10} marginTop={20} isSpaceBetween={true}>
              <LickoPayCalendarWrapper gap={10}>
                <InputWrapper>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.startDate')}</Text>
                  <LinkoCalendarInput value={startDate} setValue={setStartDate} disabled={false} placeholder={t('waiting.onSitePaymentModal.startDate')} />
                </InputWrapper>
                <InputWrapper>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('waiting.onSitePaymentModal.endDate')}</Text>
                  <LinkoCalendarInput value={endDate} setValue={setEndDate} disabled={false} placeholder={t('waiting.onSitePaymentModal.endDate')} />
                </InputWrapper>
              </LickoPayCalendarWrapper>
            </LayerWrapper>
          </LinkPayWrapper>
        </LayerTopLevelWrapper>
        <ButtonWrapper>
          <ExitButton onClick={closeFn}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('waiting.onSitePaymentModal.cancel')}</Text>
          </ExitButton>
          <SaveButton onClick={onClickRegistrationButton}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.background} cursor='pointer'>{t('waiting.onSitePaymentModal.save')}</Text>
          </SaveButton>
        </ButtonWrapper>
      </ModalView>
    </TotalModalWrapper>
  );
};

export default LinkoPayModal;

const ModalView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin: 60px 0px;
  padding: 20px 0px;

  width: 532px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 0 10px 0 10px;

  width: 100%
`;

const InfoTopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 10px;

  width: 100%;
  box-sizing: border-box;

  gap: 10px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  padding: 0 10px 0 10px;

  width: 100%;

  gap: 10px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const RowInnerWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const PrevSalesInfoBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-right: ${props => props.marginRight}px;

  width: ${props => props.width ? `${props.width}px` : '100%'};
  height: 48px;

  overflow: hidden;
  gap: 8px;
`;

const PrevSalesTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 20px;

  overflow: hidden;
`;

const PrevSalesDDayWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-item: center;

  width: 100%;
  height: 20px;

  overflow: hidden;

  border-radius: 2px;

  background-color: ${props => props.theme.line};
`;

const MembershipBoxInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const LayerTopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const LayerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: ${props => props.isSpaceBetween ? 'space-between' : 'center'};
  align-items: center;

  margin-top: ${props => props.marginTop}px;
  padding: 0 ${props => props.padding}px 0 ${props => props.padding}px;

  width: 100%;

  gap: ${props => props.gap}px;

  box-sizing: border-box;
`;

const LinkPayWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding: 0 10px;

  gap: 10px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 5px;
`;

const LickoPayUnitInputWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  gap: ${props => props.gap}px;

  flex-shrink: 0;
`;

const MembershipNameWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  overflow: hidden;
  min-width: 0;

  & > p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const SubscriptionWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  padding: 0px 5px;

  border-radius: 2px;

  background-color: ${props => props.theme.primary};
`;

const LickoPayCalendarWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: ${props => props.gap}px;
`;

const DivisionLine = styled.div`
  margin-top: ${props => props.margin}px;
  margin-bottom: ${props => props.margin}px;

  margin-left: 10px;
  margin-right: 10px;

  width: calc(100% - 20px);
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  padding: 0 10px;
  margin-top: 20px;

  width: 100%;
  box-sizing: border-box;

  gap: 10px;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0 27px 0 27px;

  height: 40px;
  flex-shrink: 0;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${props => props.theme.line};
    }
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.subKey};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${props => props.theme.subKeyHover};
    }
  }
`;

const TextWrapper = styled.div`
  display: flex;

  justify-content: space-between;
  align-items: center;

  padding: 0 20px 0 20px;

  width: ${props => props.width ? `${props.width}px` : '100%'};
`;

const CloseButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  margin-right: 30px;

  width: 24px;
  height: 24px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledPlusIcon = styled(PlusSvg)`
  width: 20px;
  height: 20px;

  transform: rotate(45deg);

  & path {
    fill: ${props => props.theme.line};
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  color: ${props => props.color};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: color 0.3s ease-in-out;
`;
